import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "@xfrontend/xui-react-plus";
import { toast } from "#/infra/Toastify";
import { useLanguage, Language } from "#/i18n/LanguageContext";

const Page = styled.div`
  padding: 24px;
  max-width: 720px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 24px;
`;

const Card = styled.section`
  background: var(--basicWrap, #1d1f24);
  border-radius: 8px;
  padding: 20px 24px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px;
`;

const SectionHint = styled.p`
  margin: 0 0 16px;
  color: var(--x-gray-400, #cfd0d3);
  font-size: 13px;
`;

const RadioRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  cursor: pointer;
  font-size: 14px;

  input {
    accent-color: var(--bs-primary, #5b8df9);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const LANGUAGE_OPTIONS: ReadonlyArray<{ value: Language; label: string }> = [
  { value: "en", label: "English" },
  { value: "zh", label: "中文" },
  { value: "ja", label: "日本語" },
  { value: "th", label: "ภาษาไทย" },
];

const Settings = (): JSX.Element => {
  const { t } = useTranslation("settings");
  const { language, setLanguage } = useLanguage();
  const [pendingLanguage, setPendingLanguage] = useState<Language>(language);

  // Re-sync the pending value when the applied language changes elsewhere
  // (e.g. legacy in-page dropdowns) or when the user revisits the page.
  useEffect(() => {
    setPendingLanguage(language);
  }, [language]);

  const isDirty = pendingLanguage !== language;

  const handleApply = () => {
    if (!isDirty) return;
    setLanguage(pendingLanguage);
    const label = LANGUAGE_OPTIONS.find((o) => o.value === pendingLanguage)?.label
      ?? pendingLanguage;
    toast.success(t("language.toastUpdated", { label }));
  };

  const handleReset = () => {
    setPendingLanguage(language);
  };

  return (
    <Page>
      <Title>{t("title")}</Title>
      <Card>
        <SectionTitle>{t("language.sectionTitle")}</SectionTitle>
        <SectionHint>
          {t("language.hint")}
        </SectionHint>
        <div role="radiogroup" aria-label={t("language.ariaLabel")}>
          {LANGUAGE_OPTIONS.map((opt) => (
            <RadioRow key={opt.value}>
              <input
                type="radio"
                name="language"
                value={opt.value}
                checked={pendingLanguage === opt.value}
                onChange={() => setPendingLanguage(opt.value)}
              />
              <span>{opt.label}</span>
            </RadioRow>
          ))}
        </div>
        <Actions>
          <Button
            variant="primary"
            onClick={handleApply}
            disabled={!isDirty}
          >
            {t("actions.apply")}
          </Button>
          <Button
            variant="secondary"
            onClick={handleReset}
            disabled={!isDirty}
          >
            {t("actions.reset")}
          </Button>
        </Actions>
      </Card>
    </Page>
  );
};

export default Settings;
